import type { ResourceConfig } from '../interfaces/ResourceConfig';
import { configStack } from './internal/configStack';

/**
 * @group Functions
 *
 * @summary
 * <blockquote>Push a new config onto the stack and return a cleanup function. The current config is always the merged result of all configs in the stack.</blockquote>
 *
 * @remarks
 * This function manages client-side resource configuration by maintaining a stack
 * of configs. Each config pushed onto the stack is merged with existing configs,
 * with later configs overriding earlier ones. The function returns a cleanup
 * callback that should be called to remove the config from the stack when no
 * longer needed (e.g., on component unmount).
 *
 * @param config - The ResourceConfig to push onto the stack
 * @returns A cleanup function that removes this config from the stack
 *
 * @example
 * ```typescript
 * // In a React component
 * useEffect(() => {
 *   const cleanup = pushClientResourceConfig({
 *     resourceId: 'my-app',
 *     environment: 'production',
 *     logging: { level: 'info' }
 *   });
 *
 *   // Cleanup removes this config from the stack
 *   return cleanup;
 * }, [config]);
 * ```
 */
/**
 * @internal
 */
export function pushClientResourceConfig(config: ResourceConfig): () => void {
  if (typeof window === 'undefined') {
    return () => {}; // No-op on server
  }

  configStack.push(config);

  // Return cleanup function to remove this config from stack
  return () => {
    const index = configStack.indexOf(config);
    if (index !== -1) {
      configStack.splice(index, 1);
    }
  };
}

